import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, TextField, Typography } from '@mui/material';
import { DOCUMENT } from '../constants/documentTheme';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeInviteEmail(value) {
  return String(value || '').trim().toLowerCase();
}

export function isValidInviteEmail(value) {
  return EMAIL_RE.test(normalizeInviteEmail(value));
}

const MemoriesInviteEmails = ({
  emails,
  onChange,
  draft,
  onDraftChange,
  onSubmit,
  submitting = false,
  showSubmit = false,
  fieldSx = {},
}) => {
  const { t } = useTranslation();
  const [localError, setLocalError] = useState('');

  const addEmail = () => {
    const email = normalizeInviteEmail(draft);
    if (!email) return;
    if (!isValidInviteEmail(email)) {
      setLocalError(t('memories.inviteEmailInvalid'));
      return;
    }
    if (emails.includes(email)) {
      setLocalError(t('memories.inviteEmailDuplicate'));
      return;
    }
    if (emails.length >= 100) {
      setLocalError(t('memories.inviteEmailLimit'));
      return;
    }
    setLocalError('');
    onChange([...emails, email]);
    onDraftChange('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addEmail();
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ letterSpacing: '0.16em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3, mb: 1 }}>
        {t('memories.invitePeople')}
      </Typography>
      <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 1.5 }}>
        {t('memories.invitePeopleHint')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          fullWidth
          type="email"
          label={t('memories.inviteEmailPlaceholder')}
          value={draft}
          onChange={(e) => {
            onDraftChange(e.target.value);
            if (localError) setLocalError('');
          }}
          onKeyDown={handleKeyDown}
          onBlur={addEmail}
          sx={fieldSx}
        />
        <Button
          type="button"
          onClick={addEmail}
          sx={{
            border: `1px solid ${DOCUMENT.rule}`,
            color: DOCUMENT.ink2,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            borderRadius: '2px',
            whiteSpace: 'nowrap',
            px: 2,
          }}
        >
          {t('memories.inviteAdd')}
        </Button>
      </Box>
      {emails.length > 0 && (
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1,
          maxHeight: 120,
          overflowY: 'auto',
        }}
        >
          {emails.map((email) => (
            <Chip
              key={email}
              label={email}
              onDelete={() => onChange(emails.filter((item) => item !== email))}
              sx={{ borderRadius: '2px', backgroundColor: '#fff', border: `1px solid ${DOCUMENT.rule}` }}
            />
          ))}
        </Box>
      )}
      {emails.length > 20 && (
        <Typography sx={{ color: DOCUMENT.ink3, fontSize: 12, mb: 1 }}>
          {t('memories.inviteQueueCount', { count: emails.length })}
        </Typography>
      )}
      {localError && <Typography sx={{ color: '#9C3B2A', fontSize: 13, mb: 1 }}>{localError}</Typography>}
      {showSubmit && (
        <Button
          type="button"
          disabled={submitting || emails.length === 0}
          onClick={onSubmit}
          sx={{
            backgroundColor: DOCUMENT.oxblood,
            color: DOCUMENT.ivory,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            borderRadius: '2px',
            mt: 0.5,
            '&:hover': { backgroundColor: '#5C1A22' },
            '&.Mui-disabled': { backgroundColor: DOCUMENT.oxblood, color: DOCUMENT.ivory, opacity: 0.45 },
          }}
        >
          {t('memories.inviteSend')}
        </Button>
      )}
    </Box>
  );
};

export default MemoriesInviteEmails;
